import { useState } from 'react';
import { Modal, Button, ConfirmDialog, EmptyState } from '../ui';
import { useShoppingCart, useUpdateCartItem, useRemoveCartItem, useClearCart } from '../../hooks/useShoppingCart';
import { useKeepStatus } from '../../hooks/useGoogleKeep';
import { KeepSetupModal } from './KeepSetupModal';
import { KeepExportModal } from './KeepExportModal';
import type { ShoppingCartItem } from '../../api/shoppingCart';
import { formatAmount, pluralizeUnit } from '../../utils/formatTime';

interface ShoppingCartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ShoppingCartModal({ isOpen, onClose }: ShoppingCartModalProps) {
  const { data, isLoading } = useShoppingCart();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const clearCart = useClearCart();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showKeepSetup, setShowKeepSetup] = useState(false);
  const [showKeepExport, setShowKeepExport] = useState(false);
  const [checkingKeepStatus, setCheckingKeepStatus] = useState(false);
  const keepStatus = useKeepStatus(checkingKeepStatus);

  const items = data?.items || [];

  // Group items by recipe_title
  const grouped = items.reduce<Record<string, ShoppingCartItem[]>>((acc, item) => {
    const key = item.recipe_title || 'Other Items';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Sort items within groups: unchecked first, then checked
  Object.values(grouped).forEach(group => {
    group.sort((a, b) => {
      if (a.checked !== b.checked) return a.checked ? 1 : -1;
      return 0;
    });
  });

  const handleToggleChecked = (item: ShoppingCartItem) => {
    updateItem.mutate({ itemId: item.id, data: { checked: !item.checked } });
  };

  const handleRemove = (itemId: string) => {
    removeItem.mutate(itemId);
  };

  const handleClear = () => {
    clearCart.mutate(undefined, {
      onSuccess: () => setShowClearConfirm(false),
    });
  };

  const handleExportToKeep = async () => {
    setCheckingKeepStatus(true);
    try {
      const status = await keepStatus.refetch();
      if (status.data?.connected) {
        setShowKeepExport(true);
      } else {
        setShowKeepSetup(true);
      }
    } catch {
      setShowKeepSetup(true);
    } finally {
      setCheckingKeepStatus(false);
    }
  };

  const handleKeepSetupSuccess = () => {
    setShowKeepSetup(false);
    setShowKeepExport(true);
  };

  const handleTokenExpired = () => {
    setShowKeepSetup(true);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Shopping List"
        size="lg"
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              {items.length} item{items.length !== 1 ? 's' : ''}
            </span>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              {items.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleExportToKeep} disabled={checkingKeepStatus}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ marginRight: '4px' }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  {checkingKeepStatus ? 'Checking...' : 'Export to Keep'}
                </Button>
              )}
              {items.length > 0 && (
                <Button variant="danger" size="sm" onClick={() => setShowClearConfirm(true)}>
                  Clear All
                </Button>
              )}
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        }
      >
        {isLoading ? (
          <p className="text-muted">Loading...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="Your shopping list is empty"
            description="Add ingredients from a recipe to get started."
            icon={
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                style={{ marginBottom: '1rem', opacity: 0.5 }}
              >
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            }
          />
        ) : (
          <div className="shopping-cart-groups">
            {Object.entries(grouped).map(([groupName, groupItems]) => (
              <div key={groupName} className="shopping-cart-group">
                <h4 className="shopping-cart-group-title">{groupName}</h4>
                {groupItems.map(item => (
                  <div key={item.id} className={`shopping-cart-item ${item.checked ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={item.checked}
                      onChange={() => handleToggleChecked(item)}
                    />
                    <span className="shopping-cart-item-text">
                      {item.amount !== null && (
                        <span className="shopping-cart-item-amount">
                          {formatAmount(item.amount)}
                          {item.unit ? ` ${pluralizeUnit(item.unit, item.amount)}` : ''}
                        </span>
                      )}
                      <span className="shopping-cart-item-name">{item.name}</span>
                      {item.notes && <span className="shopping-cart-item-notes">({item.notes})</span>}
                    </span>
                    <button
                      className="shopping-cart-item-remove"
                      onClick={() => handleRemove(item.id)}
                      aria-label={`Remove ${item.name}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClear}
        title="Clear Shopping List?"
        message="Are you sure you want to remove all items from your shopping list? This cannot be undone."
        confirmText="Clear All"
        isLoading={clearCart.isPending}
      />

      <KeepSetupModal
        isOpen={showKeepSetup}
        onClose={() => setShowKeepSetup(false)}
        onSuccess={handleKeepSetupSuccess}
      />

      <KeepExportModal
        isOpen={showKeepExport}
        onClose={() => setShowKeepExport(false)}
        itemCount={items.filter(i => !i.checked).length}
        onTokenExpired={handleTokenExpired}
        defaultCollaborators={keepStatus.data?.collaborators || []}
      />
    </>
  );
}
